import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { mountBannerAd, unmountBannerAd } from "@/services/tencentGdt";

import "./index.less";

const DEBUG_QUERY = "ylh_debug";

const stringifyDebug = (value) => {
  if (value instanceof Error) {
    return `${value.name}: ${value.message}`;
  }

  try {
    const result = JSON.stringify(value, null, 2);
    return result === undefined ? String(value) : result;
  } catch (error) {
    return `${String(value)}${error?.message ? ` (${error.message})` : ""}`;
  }
};

const TencentBannerAd = ({ scene }) => {
  const enabled = process.env.REACT_APP_YLH_ENABLED === "true";
  const appId = process.env.REACT_APP_YLH_APP_ID;
  const placementId = scene === "advance"
    ? process.env.REACT_APP_YLH_ADVANCE_BANNER_PLACEMENT_ID
    : process.env.REACT_APP_YLH_HOME_BANNER_PLACEMENT_ID;
  const containerId = `ylh-${scene}-banner`;
  const configured = enabled && Boolean(appId) && Boolean(placementId);
  const debugEnabled =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get(DEBUG_QUERY) === "1";
  const [status, setStatus] = useState("idle");
  const [debugInfo, setDebugInfo] = useState(
    debugEnabled ? "正在初始化优量汇广告，等待回调..." : ""
  );

  useEffect(() => {
    if (!configured) {
      if (debugEnabled) {
        setDebugInfo("优量汇广告未配置或当前环境未开启");
      }
      return undefined;
    }

    let disposed = false;
    setStatus("loading");
    if (debugEnabled) {
      setDebugInfo("已提交广告位请求，等待 onComplete 回调...");
    }

    mountBannerAd({
      appId,
      placementId,
      containerId,
      onComplete: (res) => {
        if (disposed) {
          return;
        }

        // 临时诊断日志：用于确认优量汇实际返回的状态码和响应内容。
        // eslint-disable-next-line no-console
        console.info("[TencentGDT] Banner callback", res);
        if (debugEnabled) {
          setDebugInfo(`优量汇 onComplete 返回：\n${stringifyDebug(res)}`);
        }

        if (Number(res?.ret) === 0) {
          setStatus("ready");
        } else {
          setStatus("failed");
          // 广告没有填充时不阻塞页面业务，也不展示空白广告框。
          // eslint-disable-next-line no-console
          console.warn("[TencentGDT] Banner load failed", res);
        }
      },
    }).catch((error) => {
      if (!disposed) {
        setStatus("failed");
        if (debugEnabled) {
          setDebugInfo(`优量汇 SDK 加载失败：\n${stringifyDebug(error)}`);
        }
        // eslint-disable-next-line no-console
        console.warn("[TencentGDT] SDK load failed", error);
      }
    });

    return () => {
      disposed = true;
      unmountBannerAd({ placementId, containerId });
    };
  }, [appId, configured, containerId, debugEnabled, placementId]);

  if (!configured) {
    return debugEnabled ? (
      <pre className="tencent-banner-ad-debug">{debugInfo}</pre>
    ) : null;
  }

  if (status === "failed" && !debugEnabled) {
    return null;
  }

  return (
    <>
      {status !== "failed" && (
        <div className="tencent-banner-ad-slot">
          <div
            id={containerId}
            className={`tencent-banner-ad ${status === "ready" ? "is-ready" : ""}`}
            aria-label="广告"
          />
        </div>
      )}
      {debugEnabled && (
        <pre className="tencent-banner-ad-debug">{debugInfo}</pre>
      )}
    </>
  );
};

TencentBannerAd.propTypes = {
  scene: PropTypes.oneOf(["home", "advance"]),
};
TencentBannerAd.defaultProps = { scene: "home" };

export default TencentBannerAd;
